import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Search, Tag, Percent, DollarSign, Calendar, CheckCircle, XCircle, Plus } from "lucide-react";
import { fetchDiscountCodes, updateDiscountCode } from "../actions/discountCodes";
import { fetchCustomersName } from "../actions/customers";
import LoadingProgress from "../components/LoadingProgress";
import DiscountCodeForm from "../forms/DiscountCodeForm";
import "../styles/DiscountCodesView.css";

class DiscountCodesView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: "",
      discountCodeModalOpen: false,
      editingDiscountCode: null,
    };

    this.handleActiveToggle = this.handleActiveToggle.bind(this);
    this.handleNewDiscountCode = this.handleNewDiscountCode.bind(this);
    this.handleEditDiscountCode = this.handleEditDiscountCode.bind(this);
    this.closeDiscountCodeModal = this.closeDiscountCodeModal.bind(this);
    this.handleDiscountCodeSubmit = this.handleDiscountCodeSubmit.bind(this);
  }

  handleActiveToggle(discountCode, e) {
    e.preventDefault();
    e.stopPropagation();
    const { updateDiscountCode } = this.props;
    const newActiveValue = !discountCode.isActive;
    updateDiscountCode(discountCode._id, { isActive: newActiveValue });
  }

  handleNewDiscountCode() {
    this.setState({
      discountCodeModalOpen: true,
      editingDiscountCode: null,
    });
  }

  handleEditDiscountCode(discountCode, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      discountCodeModalOpen: true,
      editingDiscountCode: discountCode,
    });
  }

  closeDiscountCodeModal() {
    this.setState({
      discountCodeModalOpen: false,
      editingDiscountCode: null,
    });
  }

  handleDiscountCodeSubmit() {
    this.closeDiscountCodeModal();
    // The form submission will trigger a refresh via the Redux state
  }

  componentDidMount() {
    this.props.fetchCustomersName();
    this.props.fetchDiscountCodes();
  }

  componentDidUpdate(prevProps) {
    const { discountCodesNeedRefresh } = this.props;

    if (discountCodesNeedRefresh && !prevProps.discountCodesNeedRefresh) {
      this.props.fetchDiscountCodes();
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  getFilteredDiscountCodes() {
    const { discountCodes, customers } = this.props;
    const { searchTerm } = this.state;

    if (!discountCodes) {
      return [];
    }

    let filtered = discountCodes;

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (code) =>
          code.code && code.code.toLowerCase().includes(search),
      );
    }

    // Sort by creation date (newest first)
    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  formatDiscountValue(code) {
    if (code.discountType === 'PERCENTAGE') {
      return `${code.discountValue}%`;
    } else {
      return `€${code.discountValue}`;
    }
  }

  render() {
    const { loading, error, role } = this.props;

    if (loading || !this.props.discountCodes) {
      return <LoadingProgress />;
    }

    if (error) {
      return (
        <div className="discount-codes-view">
          <div className="container mt-4">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                this.props.fetchCustomersName();
                this.props.fetchDiscountCodes();
              }}
            >
              <i className="fas fa-sync mr-2"></i>
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    const filteredCodes = this.getFilteredDiscountCodes();

    return (
      <div className="discount-codes-view">
        <div className=" mt-4">
          <div className="discount-codes-header">
            <div className="header-content">
              <div className="header-text">
                <h1>
                  <i className="fas fa-tags mr-3"></i>
                  Códigos de Descuento
                </h1>
                <span className="subtitle-inline">
                  Gestiona los códigos de descuento del sistema
                </span>
              </div>
              <div className="search-input-wrapper">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por código..."
                  value={this.state.searchTerm}
                  onChange={this.handleSearchChange}
                />
              </div>
              <div className="header-actions">
                <button
                  className="btn btn-primary"
                  onClick={this.handleNewDiscountCode}
                >
                  <Plus size={16} className="mr-2" />
                  Nuevo Código
                </button>
              </div>
            </div>
          </div>

          <div className="templates-container">
            <div className="templates-info">
              {filteredCodes.length} códigos de descuento
            </div>

            <div className="templates-grid">
              {filteredCodes.map((code) => (
                <div
                  key={code._id}
                  className="template-card"
                  style={{ cursor: 'default' }}
                >
                  <div className="template-card-header">
                    <div className="customer-avatar">
                      <Tag size={24} />
                    </div>
                    <div className="customer-info">
                      <h3 className="customer-name">{code.code}</h3>
                      <p className="customer-email">
                        {code.customer_id?.Fullname || 'Cliente desconocido'}
                      </p>
                    </div>
                  </div>

                  <div className="template-card-body">
                    <div className="template-status">
                      <div className="discount-details">
                        <small>
                          <strong>Descuento:</strong> {this.formatDiscountValue(code)}
                        </small>
                        <br />
                        <small>
                          <strong>Tipo:</strong> {code.discountType === 'PERCENTAGE' ? 'Porcentaje' : 'Fijo'}
                        </small>
                        <br />
                        <small>
                          <strong>Usos:</strong> {code.usedCount || 0}
                          {code.maxUses ? ` / ${code.maxUses}` : ' (ilimitado)'}
                        </small>
                        {code.expiresAt && (
                          <>
                            <br />
                            <small>
                              <strong>Expira:</strong> {new Date(code.expiresAt).toLocaleDateString("es-ES")}
                            </small>
                          </>
                        )}
                        {code.assigned_partner_id && (
                          <>
                            <br />
                            <small>
                              <strong>Asignado a:</strong> {code.assigned_partner_id.name}
                            </small>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="template-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="btn-edit" onClick={(e) => this.handleEditDiscountCode(code, e)}>
                      <i className="fas fa-edit mr-2"></i>
                      Editar código
                    </span>

                    <div className="template-active-toggle" onClick={(e) => this.handleActiveToggle(code, e)}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: code.isActive ? '#28a745' : '#dc3545',
                          borderRadius: '25px',
                          padding: '4px',
                          cursor: 'pointer',
                          width: '90px',
                          height: '32px',
                          position: 'relative',
                          transition: 'background-color 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                        title={code.isActive ? 'Click para desactivar' : 'Click para activar'}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            transition: 'transform 0.3s ease',
                            transform: code.isActive ? 'translateX(60px)' : 'translateX(2px)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          }}
                        />
                        <span
                          style={{
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            position: 'absolute',
                            left: code.isActive ? '8px' : 'auto',
                            right: code.isActive ? 'auto' : '8px',
                            userSelect: 'none',
                          }}
                        >
                          {code.isActive ? 'ACTIVO' : 'INACTIVO'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredCodes.length === 0 && (
              <div className="no-results">
                <i className="fas fa-search fa-3x mb-3"></i>
                <h3>No se encontraron códigos</h3>
                <p>Intenta con otro término de búsqueda</p>
              </div>
            )}
          </div>
        </div>

        {this.state.discountCodeModalOpen && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }}>
            <div className="modal-dialog modal-lg" style={{
              margin: 0,
              maxWidth: '800px',
              width: '90%'
            }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {this.state.editingDiscountCode ? 'Editar Código de Descuento' : 'Nuevo Código de Descuento'}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={this.closeDiscountCodeModal}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <DiscountCodeForm
                    editingDiscountCode={this.state.editingDiscountCode}
                    onSubmit={this.handleDiscountCodeSubmit}
                    onCancel={this.closeDiscountCodeModal}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  role: state.auth.role,
  customers: state.customers.customers,
  discountCodes: state.discountCodes.discountCodes,
  discountCodesNeedRefresh: state.discountCodes.discountCodesNeedRefresh,
  loading: state.customers.loading || state.discountCodes.loading,
  error: state.customers.error || state.discountCodes.error,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchCustomersName,
      fetchDiscountCodes,
      updateDiscountCode,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(DiscountCodesView);